import React from 'react';

type Point = [number, number];

const ONE_FINGER = {
    outline:
        'M-5 38V5A5 5 0 0 1 5 5V22A4 4 0 0 1 13 22A4 4 0 0 1 21 24A4 4 0 0 1 29 28V44C29 56 21 64 8 64C2 64 -3 61 -7 56L-20 36A5.2 5.2 0 0 1 -11.5 30Z',
    detail: 'M5 22V31M13 22V28M21 24V30',
};

const TWO_FINGERS = {
    outline:
        'M-10 38V7A5 5 0 0 1 0 7V5A5 5 0 0 1 10 5V24A4 4 0 0 1 18 25A4 4 0 0 1 26 29V44C26 56 18 64 5 64C-1 64 -6 61 -10 56L-23 36A5.2 5.2 0 0 1 -14.5 30Z',
    detail: 'M0 7V33M10 24V31M18 25V30',
};

const PINCH_PALM =
    'M6 32V28A4 4 0 0 1 14 28A4 4 0 0 1 22 30A4 4 0 0 1 30 34V48C30 60 22 68 9 68';
const INDEX_PIVOT: Point = [0, 36];
const THUMB_PIVOT: Point = [-2, 55];

const rotate = ([x, y]: Point, degrees: number, [px, py]: Point): Point => {
    const cos = Math.cos((degrees * Math.PI) / 180);
    const sin = Math.sin((degrees * Math.PI) / 180);

    return [px + x * cos - y * sin, py + x * sin + y * cos];
};

const toPath = ([x, y]: Point) => `${x.toFixed(1)} ${y.toFixed(1)}`;

const renderPinch = (spread: number) => {
    const indexAngle = -28 + spread * 25;
    const thumbAngle = -30 - spread * 25;
    const indexTip = rotate([0, -30], indexAngle, INDEX_PIVOT);
    const thumbTip = rotate([0, -26], thumbAngle, THUMB_PIVOT);
    const indexBase = toPath(rotate([-5, -4], indexAngle, INDEX_PIVOT));
    const thumbUpper = toPath(rotate([5.5, -10], thumbAngle, THUMB_PIVOT));
    const thumbLower = toPath(rotate([-5.5, 0], thumbAngle, THUMB_PIVOT));
    const webStart = toPath(rotate([5.5, -1], thumbAngle, THUMB_PIVOT));
    const webEnd = toPath(rotate([-5, 6], indexAngle, INDEX_PIVOT));
    const heel = toPath(rotate([-5.5, 7], thumbAngle, THUMB_PIVOT));
    const palm = `${PINCH_PALM}C3 68 ${heel} ${thumbLower}`;
    const web = `C${webStart} ${webEnd} ${indexBase}`;

    return (
        <>
            <circle
                cx={indexTip[0]}
                cy={indexTip[1]}
                r={10}
                className="contact"
            />
            <circle
                cx={thumbTip[0]}
                cy={thumbTip[1]}
                r={10}
                className="contact"
            />
            <path
                d="M-5.5 0L-4.5 -26A4.5 4.5 0 0 1 4.5 -26L5.5 3"
                className="hand"
                transform={`translate(${toPath(THUMB_PIVOT)}) rotate(${thumbAngle})`}
            />
            <rect
                x={-5}
                y={-35}
                width={10}
                height={42}
                rx={5}
                className="hand"
                transform={`translate(${toPath(INDEX_PIVOT)}) rotate(${indexAngle})`}
            />
            <path d={`${palm}L${thumbUpper}${web}Z`} className="palm" />
            <path
                d={`${palm}M${thumbUpper}${web}M14 28V34M22 30V35`}
                className="hand-detail"
            />
        </>
    );
};

export const Finger = ({
    x,
    y,
    fingers = 1,
    spread,
}: {
    x: number;
    y: number;
    fingers?: 1 | 2;
    spread?: number;
}) => {
    const hand = fingers === 2 ? TWO_FINGERS : ONE_FINGER;

    return (
        <g transform={`translate(${x},${y})`}>
            {spread === undefined ? (
                <>
                    <path d={hand.outline} className="hand" />
                    <path d={hand.detail} className="hand-detail" />
                </>
            ) : (
                renderPinch(spread)
            )}
        </g>
    );
};
